// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAdminData } from './useAdminData';
import api from '../../utils/api';

import OverviewPanel    from './adminpanel/OverviewPanel';
import UsersPanel       from './adminpanel/UsersPanel';
import ListingsPanel    from './adminpanel/ListingsPanel';
import ModerationPanel  from './adminpanel/ModerationPanel';
import SalesPanel       from './adminpanel/SalesPanel';
import InquiriesPanel   from './adminpanel/InquiriesPanel';
import SettingsPanel    from './adminpanel/SettingsPanel';

const NAV = [
  { key: 'overview', icon: 'dashboard', label: 'Overview', showCount: true },
  { key: 'users',      icon: 'manage_accounts',  label: 'Users'      },
  { key: 'listings',   icon: 'pets',             label: 'Listings'   },
  { key: 'moderation', icon: 'gavel',            label: 'Moderation' },
  { key: 'sales',      icon: 'payments',         label: 'Sales'      },
  { key: 'inquiries',  icon: 'forum',            label: 'Inquiries'  },
  { key: 'settings',   icon: 'settings',         label: 'Settings'   },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [active, setActive] = useState(() => {
    return window.location.hash.replace('#', '') || 'overview';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const [highlightAnimal, setHighlightAnimal] = useState(null);
  const [highlightProduct, setHighlightProduct] = useState(null);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get(`/api/admin/notifications?userId=${user.id}`);
      setNotifList(data);
    } catch (err) {
      console.error('fetchNotifications error:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifList.filter(n => !n.is_read).length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifs && !e.target.closest('.notif-wrapper')) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifs]);

  const setActiveTab = (tab) => {
    window.location.hash = tab;
    setActive(tab);
  };

  useEffect(() => {
    const onPopState = () => {
      if (!window.location.pathname.startsWith('/admin')) return;
      const tab = window.location.hash.replace('#', '') || 'overview';
      setActive(tab);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const [search, setSearch] = useState('');
  
  const {
    users, userStats, animals, sales, salesStats, inquiries,
    loadingUsers, loadingData, 
    error,
    fetchUsers, fetchStats, fetchAnimals, fetchSales, fetchInquiries, handleModAction,
    butcherProducts, fetchButcherProducts, handleProductModAction,
    breedStats, recentActivity, fetchBreedStats, fetchRecentActivity,
    platformStats, fetchPlatformStats, auctions, fetchAuctions, handleAuctionApproval,handleRequestChanges,
  } = useAdminData();

  // ── Auth guard ─────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') navigate('/login');
  }, [user, navigate, authLoading]);

  // ── Load data on tab change ────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (active === 'overview')   { fetchStats(); fetchBreedStats(); fetchRecentActivity(); fetchPlatformStats(); fetchAnimals('pending'); fetchButcherProducts('pending'); }
    if (active === 'users')      { fetchUsers(search); }
    if (active === 'listings')   { fetchAnimals(); fetchButcherProducts(); }
    if (active === 'moderation') { fetchAnimals(); fetchButcherProducts(); fetchAuctions();}
    if (active === 'sales')      { fetchSales(); }
    if (active === 'inquiries')  { fetchInquiries(); }
  }, [active, authLoading, fetchStats, fetchAnimals, fetchButcherProducts, fetchBreedStats, fetchRecentActivity, fetchPlatformStats, fetchUsers, search, fetchSales, fetchInquiries]);

  // ── Search debounce ─────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (active !== 'users') return;
    const t = setTimeout(() => fetchUsers(search), 400);
    return () => clearTimeout(t);
  }, [search, authLoading, active, fetchUsers]);

  // ── All hooks above this line — early returns are safe below ──

  const pendingCount = animals.filter(a => a.status === 'pending').length
                   + butcherProducts.filter(p => p.status === 'pending').length
                   + auctions.filter(a => a.approval_status === 'pending_approval').length;
  const handleLogout = () => { logout(); navigate('/'); };

  // Render nothing while auth state is being read from storage
  if (authLoading) return null;

  // ── Panel map ──────────────────────────────────────────
  const PANELS = {
    overview:   <OverviewPanel   userStats={userStats} 
                                 animals={animals}
                                 butcherProducts={butcherProducts}
                                 pendingCount={pendingCount}
                                 breedStats={breedStats}
                                 recentActivity={recentActivity}
                                 platformStats={platformStats}
                                 onModAction={handleModAction}
                                 onProductModAction={handleProductModAction}
                                 onViewAll={() => setActiveTab('moderation')} />,

    users:      <UsersPanel      search={search} setSearch={setSearch} />,

    listings:   <ListingsPanel   animals={animals}
                                butcherProducts={butcherProducts}
                                loading={loadingData} />,

    moderation: <ModerationPanel animals={animals}
                                butcherProducts={butcherProducts}
                                auctions={auctions}
                                loading={loadingData}
                                onAction={handleModAction}
                                onProductAction={handleProductModAction}
                                onAuctionApproval={handleAuctionApproval}
                                onRequestChanges={handleRequestChanges}
                                highlightAnimal={highlightAnimal}
                                highlightProduct={highlightProduct}/>,

    sales:      <SalesPanel      sales={sales} salesStats={salesStats}
                                loading={loadingData} />,

    inquiries:  <InquiriesPanel  inquiries={inquiries} loading={loadingData} />,

    settings:   <SettingsPanel />,
  };

  // ──────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-[#f4f6f4] font-body">

      {/* ── Mobile overlay ───────────────────────────── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden"
             onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ──────────────────────────────────── */}
      <aside className={`fixed left-0 top-0 h-screen w-60 bg-white border-r
                         border-outline-variant/20 flex flex-col z-40 shadow-sm
                         transition-transform duration-300
                         ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                         md:translate-x-0`}>

        <div className="px-5 py-5 border-b border-outline-variant/10">
          <img src="/assets/elite-goats.png" alt="Elite Goats"
               className="h-10 w-auto object-contain" />
          <p className="text-[10px] font-bold text-outline uppercase tracking-widest mt-1">
            Admin Console
          </p>
        </div>

        <nav className="flex-1 px-3 mt-4 space-y-0.5">
          {NAV.map((item) => (
            <button key={item.key}
              onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
                          font-semibold transition-all ${
                active === item.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
              }`}>
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
              <span className="flex-1 text-left">{item.label}</span>
              {(item.key === 'moderation' || (item.key === 'overview' && item.showCount)) && pendingCount > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key
                    ? 'bg-white/20 text-white'
                    : 'bg-tertiary text-white'
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-outline-variant/10 space-y-1">
          <button onClick={() => navigate('/', { state: { viewHome: true } })}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs
                       font-medium text-on-surface-variant hover:bg-surface-container transition-all">
            <span className="material-symbols-outlined text-[18px]">home</span>
            Back to Site
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
                       font-semibold text-red-600 hover:bg-red-50 transition-all">
            <span className="material-symbols-outlined text-[20px]">logout</span>
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────── */}
      <main className="flex-1 md:ml-60 min-h-screen pt-20 px-4 md:px-8 pb-8">

        {/* ── Top Header ───────────────────────────────── */}
        <header className="fixed top-0 left-0 md:left-60 right-0 z-40 h-16
                           bg-white/80 backdrop-blur-md border-b border-outline-variant/20
                           flex items-center justify-between px-4 md:px-8 gap-4">

          {/* Hamburger — mobile only */}
          <button className="md:hidden p-2 rounded-xl hover:bg-surface-container
                             text-secondary flex-shrink-0"
                  onClick={() => setSidebarOpen(true)}>
            <span className="material-symbols-outlined text-[24px]">menu</span>
          </button>
          
          <div className="flex-1 hidden md:block" />

          <div className="flex items-center gap-2 md:gap-5 flex-shrink-0">
            <div className="flex items-center gap-1">
              <div className="relative notif-wrapper">
                <button 
                  onClick={async () => {
                    setShowNotifs(!showNotifs);
                    if (!showNotifs && user?.id) {
                      await api.patch('/api/admin/notifications/read', { userId: user.id });
                      fetchNotifications();
                    }
                  }}
                  className="relative p-2 text-on-surface-variant hover:bg-surface-container rounded-full transition-colors">
                  <span className="material-symbols-outlined text-[22px]">notifications</span>
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full 
                                    text-white text-[10px] font-black flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {showNotifs && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-xl 
                                  border border-outline-variant/20 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-outline-variant/10 flex justify-between items-center">
                      <span className="font-bold text-sm">Notifications</span>
                      <button onClick={async () => {
                        await api.patch('/api/admin/notifications/read', { userId: user.id });
                        fetchNotifications();
                      }} className="text-[11px] text-outline hover:text-red-500">Clear all</button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifList.length === 0 ? (
                        <p className="text-center text-outline text-sm py-8">No notifications</p>
                      ) : notifList.map(n => (
                        <div key={n.id} 
                          onClick={() => {
                            setShowNotifs(false);

                            if (n.title === '🛒 New Order Received') {
                              setActiveTab('sales');
                              fetchSales();   
                            } else if (n.title === 'New Animal Listed') {
                              setHighlightAnimal(n.body?.split(' was listed')[0]);
                              setActiveTab('moderation');
                              fetchAnimals();   
                            } else if (n.title === 'New Product Listed') {
                              setHighlightProduct(n.body);   
                              setActiveTab('moderation'); 
                              fetchButcherProducts(); 
                            } else {
                              setActiveTab('moderation');
                            }
                          }}
                          className="px-4 py-3 border-b border-outline-variant/10 hover:bg-surface-container-low cursor-pointer">
                          <p className="text-sm font-semibold text-on-surface">{n.title}</p>
                          <p className="text-xs text-outline mt-0.5">{n.body}</p>
                          <p className="text-[10px] text-outline-variant mt-1">
                            {new Date(n.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button className="p-2 text-on-surface-variant hover:bg-surface-container
                                 rounded-full transition-colors hidden md:block">
                <span className="material-symbols-outlined text-[22px]">mail</span>
              </button>
            </div>

            <div className="h-7 w-px bg-outline-variant/40 hidden md:block" />

            <button className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full
                               border-2 border-outline-variant/30 text-emerald-700
                               font-semibold text-sm hover:bg-emerald-50 transition-all">
              <span className="material-symbols-outlined text-[18px]">download</span>
              Export Reports
            </button>
            <button className="hidden md:block px-5 py-2 rounded-full bg-primary
                               text-on-primary font-bold text-sm hover:opacity-90
                               transition-all shadow-sm">
              Manage Platform
            </button>

            <div className="h-7 w-px bg-outline-variant/40 hidden md:block" />

            <div className="flex items-center gap-3 cursor-pointer group">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-bold text-on-surface leading-tight">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-[10px] text-secondary uppercase tracking-wider">
                  System Administrator
                </p>
              </div>
              {user?.profile_image ? (
                <img src={user.profile_image} alt="avatar"
                    className="w-9 h-9 rounded-full object-cover border-2
                                border-emerald-100 group-hover:ring-2
                                group-hover:ring-primary/30 transition-all" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-primary flex items-center
                                justify-center text-white font-black text-sm border-2
                                border-emerald-100 group-hover:ring-2
                                group-hover:ring-primary/30 transition-all">
                  {(user?.name || 'A')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Error banner ─────────────────────────────── */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3
                          rounded-xl text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        {/* ── Active panel ─────────────────────────────── */}
        {PANELS[active]}

      </main>
    </div>
  );
}